import "../ImportData/style.scss";

import { CreateInvoiceModalContent } from "components/ImportData/CreateInvoiceModal";
import ModalBox from "components/Modal";
import useAPIPagination from "hooks/useAPIPagination";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { clearInvoice, createBexioInvoice } from "store/bexioStore";
import type { InvoicePercentage } from "store/invoicePercentages";
import type { TFetchStatus } from "store/types";

import type { IFile } from "../../store/filesHistorySlice";
import { fetchImportedFiles } from "../../store/filesHistorySlice";
import { useAppDispatch } from "../../store/store";
import type { DTOBexioGETContact, DTOBexioGetInvoice, TCalculationMethodRender } from "../../types";
import { Spinner } from "../shared/Spinner";
import StyledButton from "../shared/StyledButton";

interface ISendInvoiceModal {
    open: boolean;
    onClose: () => void;

    afterImportInfo: IFile;
    bexioContacts: DTOBexioGETContact[];
    createdInvoice: Partial<DTOBexioGetInvoice>;
    failureReasonOnCreateInvoice: string;
    invoicePercentages: InvoicePercentage[];
}

/**
 * Modal to send an invoice
 *
 * @param open {boolean} - Classic modal prop to open or close the modal
 * @param onClose {() => void} - Function to close the modal
 * @param afterImportInfo {IFile} - Information about the import
 * @param bexioContacts {DTOBexioGETContact[]} - List of bexio contacts for the select
 * @constructor
 */
export const SendInvoiceModal = ({
    open,
    onClose,
    afterImportInfo,
    bexioContacts,
    createdInvoice,
    failureReasonOnCreateInvoice,
    invoicePercentages,
}: ISendInvoiceModal) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const { handleRequest } = useAPIPagination({ type: undefined });

    const [selectedBexioContact, setSelectedBexioContact] = useState<DTOBexioGETContact | null>(null);
    const [generationStatus, setGenerationStatus] = useState<TFetchStatus>("idle");
    // For now, we're only using file. In the future this state must be initialised with null for proper checks:
    const [calculationMethodForInvoice, setCalculationMethodForInvoice] = useState<"crealo" | "file" | null>("file");

    const [calculationMethods, setCalculationMethods] = useState<Array<TCalculationMethodRender>>([
        { id: "crealo", name: t("ImportData.CalculationCrealo") },
        { id: "file", name: t("ImportData.CalculationFile") },
    ]);

    useEffect(() => {
        setGenerationStatus("idle");
    }, [open]);

    useEffect(() => {
        switch (generationStatus) {
            case "succeeded":
                handleRequest({ defaultType: "importFiles" });

                handleClose();
                break;
        }
    }, [generationStatus]);

    const handleClose = () => {
        clearInvoice();

        onClose();
    };

    const handleSendInvoice = async () => {
        setGenerationStatus("pending");

        if (!selectedBexioContact || !calculationMethodForInvoice) {
            setGenerationStatus("failed");
            return;
        }

        try {
            if (!afterImportInfo.processingJobId) return;
            const response = await dispatch(
                createBexioInvoice({
                    contactId: selectedBexioContact.id,
                    processingJobId: afterImportInfo.processingJobId,
                    importId: afterImportInfo.id,
                    // calculationMethodForInvoice: calculationMethodForInvoice, // Not using Crealo CA for now.
                    calculationMethodForInvoice: "file",
                }),
            );

            if (response.meta.requestStatus === "fulfilled") {
                setGenerationStatus("succeeded");
            } else {
                setGenerationStatus("failed");
            }
        } catch (error) {
            console.error("error", error);
            setGenerationStatus("failed");
        }
    };

    return (
        <ModalBox
            open={open}
            onClose={handleClose}
            style={{ maxWidth: "800px" }}
            mainTitle={t("Invoice.GenerateInvoice")}
            subTitle={t("Invoice.Subtitle")}
        >
            <div
                style={
                    {
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "1rem",
                    } as React.CSSProperties
                }
                // style={style.importDataWrapper}
            >
                {["idle", "failed"].includes(generationStatus) && (
                    <div>
                        <main className="import-data-main">
                            <CreateInvoiceModalContent
                                afterImportInfo={{
                                    revenue: afterImportInfo.metadata.revenue,
                                    invoiceable: afterImportInfo.metadata.invoiceable,
                                    processingJobId: afterImportInfo.processingJobId,
                                    id: afterImportInfo.id,
                                }}
                                bexioContacts={bexioContacts}
                                calculationMethods={calculationMethods}
                                setSelectedBexioContact={setSelectedBexioContact}
                                setTurnoverCalculationChoice={setCalculationMethodForInvoice}
                                nbOfImportedLines={afterImportInfo.numberOfLines}
                                createdInvoice={createdInvoice}
                                failureReasonOnCreateInvoice={failureReasonOnCreateInvoice}
                                invoicePercentages={invoicePercentages}
                            />
                        </main>
                    </div>
                )}

                {generationStatus === "pending" && (
                    <div
                        style={
                            {
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "1rem",
                            } as React.CSSProperties
                        }
                    >
                        <Spinner />
                        <span style={{ marginTop: "1rem" }}>{t("Invoice.Pending")}</span>
                    </div>
                )}

                {generationStatus === "succeeded" && (
                    <div
                        style={
                            {
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "1rem",
                            } as React.CSSProperties
                        }
                    >
                        <span>{t("Invoice.Created")}</span>
                    </div>
                )}

                <StyledButton
                    sx={{
                        alignSelf: "flex-end",
                    }}
                    onClick={handleSendInvoice}
                    disabled={generationStatus === "pending" || !selectedBexioContact}
                    buttonType="primary"
                >
                    {generationStatus === "succeeded" ? t("Stock.Close") : t("Invoice.GenerateInvoice")}
                </StyledButton>
            </div>
        </ModalBox>
    );
};
